import throttle from 'lodash.throttle';
import { useState, useEffect, useRef, useCallback } from 'react';
import {
  StyleSheet,
  Animated,
  ScrollView,
  TouchableOpacity,
  StyleProp,
  TextStyle,
} from 'react-native';
import Modal from 'react-native-modal';
import Typewriter from 'typewriter-effect';

import { PrimaryButton } from './Button';
import { Image } from './Image';
import MarkdownPreviewer from './MarkdownPreviewer';
import ModalCloseButton from './ModalCloseButton';
import Row, { TransparentRow } from './Row';
import { TextBodySmall } from './StyledText';
import { FormInput, FormInputProps } from './StyledTextInput';
import { TransparentView, View } from './Themed';
import { ProfileAvatar } from './profile/Avatar';
import { postChat } from '../api/chat';
import Colors from '../constants/Colors';
import useBlockBodyScroll from '../hooks/useBlockBodyScroll';
import { useParent } from '../hooks/useChild';
import useColorScheme from '../hooks/useColorScheme';
import { useModalState } from '../hooks/useModalState';

function TypingIndicator() {
  const dot1 = useRef(new Animated.Value(0)).current;
  const dot2 = useRef(new Animated.Value(0)).current;
  const dot3 = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    const animate = () => {
      Animated.sequence([
        Animated.parallel([
          Animated.timing(dot1, {
            toValue: 1,
            duration: 400,
            useNativeDriver: true,
          }),
          Animated.timing(dot2, {
            toValue: 1,
            duration: 400,
            delay: 200,
            useNativeDriver: true,
          }),
          Animated.timing(dot3, {
            toValue: 1,
            duration: 400,
            delay: 400,
            useNativeDriver: true,
          }),
        ]),
        Animated.parallel([
          Animated.timing(dot1, {
            toValue: 0,
            duration: 400,
            useNativeDriver: true,
          }),
          Animated.timing(dot2, {
            toValue: 0,
            duration: 400,
            delay: 200,
            useNativeDriver: true,
          }),
          Animated.timing(dot3, {
            toValue: 0,
            duration: 400,
            delay: 400,
            useNativeDriver: true,
          }),
        ]),
      ]).start(() => animate());
    };

    animate();

    return () => {
      dot1.stopAnimation();
      dot2.stopAnimation();
      dot3.stopAnimation();
    };
  }, [dot1, dot2, dot3]);

  return (
    <TransparentView style={styles.typingIndicator}>
      <Animated.View style={[styles.typingDot, { opacity: dot1 }]} />
      <Animated.View style={[styles.typingDot, { opacity: dot2 }]} />
      <Animated.View style={[styles.typingDot, { opacity: dot3 }]} />
    </TransparentView>
  );
}

const assistantAvatar = require('../assets/images/tina.jpg');

function MarkdownTypewriter({
  text,
  fontSizeStyles,
  onUpdate,
  skipAnimation = false,
  onTypingComplete,
}: {
  text: string;
  fontSizeStyles: StyleProp<TextStyle>;
  onUpdate: () => void;
  skipAnimation?: boolean;
  onTypingComplete?: () => void;
}) {
  const [displayedText, setDisplayedText] = useState('');
  const [currentIndex, setCurrentIndex] = useState(0);
  const throttledOnUpdate = useRef(throttle(onUpdate, 200)).current;

  const processedText = text.replace(/\\n/g, '\n');
  const words = processedText.split(' ');

  useEffect(() => {
    if (skipAnimation) {
      setDisplayedText(processedText);
      setCurrentIndex(words.length);
      onTypingComplete?.();
      return;
    }

    if (currentIndex < words.length) {
      const timer = setTimeout(() => {
        const wordsToShow = words.slice(0, currentIndex + 1);
        setDisplayedText(wordsToShow.join(' '));
        setCurrentIndex(currentIndex + 1);
      }, 60);

      return () => clearTimeout(timer);
    } else if (currentIndex === words.length && currentIndex > 0) {
      onTypingComplete?.();
    }
  }, [currentIndex, words, skipAnimation, processedText, onTypingComplete]);

  useEffect(() => {
    throttledOnUpdate();
  }, [displayedText]);

  useEffect(() => {
    return () => {
      throttledOnUpdate.cancel();
    };
  }, []);

  return (
    <MarkdownPreviewer fontSizeStyles={fontSizeStyles}>
      {displayedText}
    </MarkdownPreviewer>
  );
}

function Avatar({ role }: { role: 'user' | 'assistant' }) {
  const { firstName } = useParent();

  if (role === 'assistant') {
    return (
      <Image
        source={assistantAvatar}
        style={[styles.avatar, styles.avatarAssistant]}
      />
    );
  }

  const initial = firstName?.charAt(0)?.toUpperCase() || 'U';

  return (
    <ProfileAvatar
      letter={initial}
      style={[styles.avatar, styles.avatarUser]}
      textComponent={TextBodySmall}
    />
  );
}

export function TinaAi() {
  const { isModalVisible, showModal, closeModal } = useModalState();
  const scrollViewRef = useRef<ScrollView>(null);
  const [messages, setMessages] = useState<
    { role: 'user' | 'assistant'; content: string }[]
  >([
    {
      role: 'assistant',
      content: "Hi! I'm **Tina**. How can I help you find child care?",
    },
  ]);
  const [lastTypedIndex, setLastTypedIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [input, setInput] = useState('');
  const [previousResponseId, setPreviousResponseId] = useState<
    string | undefined
  >(undefined);
  useBlockBodyScroll(isModalVisible);

  const handleScrollToEnd = useCallback(() => {
    if (scrollViewRef.current) {
      scrollViewRef.current?.scrollToEnd({
        animated: true,
      });
    }
  }, []);

  const handleTypingComplete = useCallback((messageIndex: number) => {
    setLastTypedIndex(messageIndex);
  }, []);

  const handleSendMessage = async () => {
    if (!input.trim() || isLoading) return;
    setMessages([...messages, { role: 'user', content: input }]);
    setInput('');
    setIsLoading(true);
    const response = await postChat(input, previousResponseId);
    setIsLoading(false);
    setPreviousResponseId(response.id);
    setMessages([
      ...messages,
      { role: 'user', content: input },
      { role: 'assistant', content: response.text },
    ]);
  };

  useEffect(() => {
    if (scrollViewRef.current) {
      setTimeout(() => {
        scrollViewRef.current?.scrollToEnd({ animated: true });
      }, 100);
    }
  }, [messages, isLoading]);

  return (
    <TransparentView style={styles.container}>
      <TinaAiButton onPress={showModal} />
      <Modal
        useNativeDriver
        isVisible={isModalVisible}
        onBackdropPress={closeModal}
        style={{ margin: 0 }}
        hideModalContentWhileAnimating
        testID="tina-ai-modal-container"
      >
        <View style={[styles.modalContainer]} testID="tina-ai-modal">
          <ModalCloseButton onPress={closeModal} absolute />
          <ScrollView
            ref={scrollViewRef}
            style={styles.messagesContainer}
            showsVerticalScrollIndicator={false}
          >
            {messages.map((message, index) => (
              <Row
                key={index}
                style={[
                  styles.messageWrapper,
                  message.role === 'user'
                    ? styles.userMessageWrapper
                    : styles.assistantMessageWrapper,
                ]}
              >
                {message.role === 'assistant' && <Avatar role="assistant" />}
                <View
                  style={[
                    styles.messageBubble,
                    message.role === 'user'
                      ? styles.userMessageBubble
                      : styles.assistantMessageBubble,
                  ]}
                >
                  {message.role === 'assistant' ? (
                    <MarkdownTypewriter
                      text={message.content}
                      onUpdate={handleScrollToEnd}
                      fontSizeStyles={styles.messageText}
                      skipAnimation={index <= lastTypedIndex}
                      onTypingComplete={() => handleTypingComplete(index)}
                    />
                  ) : (
                    <TextBodySmall style={styles.messageText}>
                      {message.content}
                    </TextBodySmall>
                  )}
                </View>
                {message.role === 'user' && <Avatar role="user" />}
              </Row>
            ))}
            {isLoading && (
              <Row
                style={[styles.messageWrapper, styles.assistantMessageWrapper]}
              >
                <Avatar role="assistant" />
                <View
                  style={[styles.messageBubble, styles.assistantMessageBubble]}
                >
                  <TypingIndicator />
                </View>
              </Row>
            )}
          </ScrollView>
          <TransparentRow style={styles.inputRow}>
            <TinaAiInput
              onChangeText={setInput}
              value={input}
              onSubmitEditing={handleSendMessage}
            />
            <PrimaryButton
              disabled={isLoading}
              title="Send"
              style={styles.sendButton}
              onPress={handleSendMessage}
            />
          </TransparentRow>
        </View>
      </Modal>
    </TransparentView>
  );
}

function TinaAiInput(props: FormInputProps) {
  return (
    <FormInput
      style={styles.input}
      placeholder="Ask Tina to help you find child care"
      returnKeyType="send"
      {...props}
    />
  );
}

function TinaAiButton({ onPress }: { onPress: () => void }) {
  const theme = useColorScheme();
  const { firstName } = useParent();

  return (
    <TouchableOpacity
      style={[
        styles.inputButton,
        {
          shadowRadius: 12,
          shadowOpacity: 0.8,
          shadowOffset: {
            width: 0,
            height: 4,
          },
          shadowColor: Colors[theme].shadow,
        },
      ]}
      onPress={onPress}
    >
      <Row>
        <Image
          source={assistantAvatar}
          style={[styles.avatar, styles.avatarAssistant]}
        />
        <TextBodySmall style={styles.inputButtonText}>
          <Typewriter
            options={{
              strings: [
                `Hi ${firstName}! I'm Tina! Let me help you book child care!`,
              ],
              autoStart: true,
              loop: true,
              delay: 50,
              deleteSpeed: 10,
              cursor: '|',
            }}
          />
        </TextBodySmall>
      </Row>
    </TouchableOpacity>
  );
}

const styles = StyleSheet.create({
  container: {
    marginTop: 10,
  },
  input: {
    width: 400,
    marginBottom: 0,
  },
  modalContainer: {
    padding: 40,
    borderRadius: 10,
    alignSelf: 'center',
    width: '100%',
    height: '100%',
    maxWidth: 580,
    maxHeight: 600,
  },
  sendButton: {
    marginLeft: 10,
    paddingHorizontal: 25,
  },
  messagesContainer: {
    marginTop: 10,
    flex: 1,
    padding: 15,
    borderWidth: 1,
    borderColor: Colors.light.lines,
    borderRadius: 12,
    marginBottom: 15,
  },
  messageWrapper: {
    marginVertical: 4,
  },
  userMessageWrapper: {
    justifyContent: 'flex-end',
  },
  assistantMessageWrapper: {
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
  },
  messageBubble: {
    maxWidth: '80%',
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 20,
  },
  userMessageBubble: {
    backgroundColor: Colors.light.accent,
  },
  assistantMessageBubble: {
    backgroundColor: Colors.light.backgroundSecondary,
  },
  messageText: {
    fontSize: 14,
    lineHeight: 20,
    fontFamily: 'Mont',
  },
  inputRow: {
    marginTop: 10,
  },
  typingIndicator: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 8,
  },
  typingDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: Colors.light.textSecondary,
    marginHorizontal: 2,
  },
  avatar: {
    width: 36,
    height: 36,
    borderRadius: 18,
  },
  avatarAssistant: {
    marginRight: 6,
    marginTop: 3,
  },
  avatarUser: {
    marginLeft: 6,
    marginBottom: 0,
  },
  inputButton: {
    width: 450,
    marginBottom: 0,
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderRadius: 20,
    alignItems: 'center',
    justifyContent: 'center',
  },
  inputButtonText: {
    fontSize: 14,
    lineHeight: 20,
    marginLeft: 6,
  },
});
